import React from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, Gift, Sparkles } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { namedRegion, useBrand, useMarket, useTenantPath } from "@/lib/brand";
import { lbl } from "@/lib/terms";
import { mediaUrl } from "@/lib/api";
import EstateSellLet from "@/pages/sites/EstateSellLet";
import { isEstate, isPreviewing } from "@/lib/sitePreview";

/**
 * AgencyLet — the tenant's "Let your property" page for landlords.
 * Mirrors AgencySell's structure/data-shape (brand.letting) so the two
 * stay easy to maintain together as one template pattern. In a US market
 * this reads as the "Rentals & property management" page.
 */
function AgencyLetClassic() {
  const { slug: urlSlug } = useParams();
  const brand = useBrand();
  const market = useMarket();
  const tenantPath = useTenantPath();
  const biz = brand.business || {};
  const tenantSlug = urlSlug || brand.tenant_slug || "foundation";
  const l = brand.letting || {};
  const fees = l.fee_options || [];
  const isUs = market === "us";

  const contactHref = tenantPath("contact");
  const valuationHref = tenantPath("valuation");

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SeoHead
        title={biz.name
          ? (isUs
            ? `Landlords: Property Management with ${biz.name} | ${namedRegion(brand) || "Local Rentals"}`
            : `Landlords: Let Your Property with ${biz.name} | ${namedRegion(brand) || "Local Lettings"}`)
          : undefined}
        description={l.intro || (isUs
          ? `Rent out your property with ${biz.name || "a local brokerage"}, professional tenant screening, full management and Fair Housing-compliant leasing.`
          : `Let your property with ${biz.name || "a local agent"}, professional tenant vetting, full management and rent protection available.`)}
        image={brand.assets?.hero_image_url ? mediaUrl(brand.assets.hero_image_url) : undefined}
      />
      <SiteHeader />
      <main className="flex-1">
        <section className="mx-auto max-w-6xl px-6 lg:px-10 py-16 lg:py-24">
          <div className="overline">{isUs ? "Property management with" : "Letting with"} {biz.name || "us"}</div>
          <h1 className="font-serif-display text-5xl lg:text-6xl text-primary mt-6 tracking-tight leading-[1.05] max-w-3xl">
            {l.headline || (isUs
              ? `Rent out your property with ${biz.name || "a team you can trust"}.`
              : `Let your property with ${biz.name || "a team you can trust"}.`)}
          </h1>
          {l.sub && <p className="text-lg text-muted-foreground mt-6 max-w-2xl leading-relaxed">{l.sub}</p>}
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to={contactHref} data-testid="let-cta-contact">
              <Button size="lg" className="h-12 px-7 rounded-sm sharp-btn">
                {lbl("rental_valuation_cta", market)} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
          {l.promo && (
            <div className="mt-8 inline-flex items-start gap-3 border border-accent/40 bg-accent/5 rounded-sm px-5 py-4 max-w-xl">
              <Gift className="w-4 h-4 text-accent-ink shrink-0 mt-0.5" />
              <span className="text-sm text-primary/90 leading-relaxed">{l.promo}</span>
            </div>
          )}
        </section>

        {l.intro && (
          <section className="border-t border-border bg-secondary/15">
            <div className="mx-auto max-w-4xl px-6 lg:px-10 py-14 text-center">
              <p className="text-lg text-primary/85 leading-relaxed">{l.intro}</p>
            </div>
          </section>
        )}

        {l.why_us?.length > 0 && (
          <section className="border-t border-border py-16 lg:py-24">
            <div className="mx-auto max-w-6xl px-6 lg:px-10">
              <div className="overline text-center">{lbl("landlords_choose", market)}</div>
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
                {l.why_us.map((w, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-accent-ink shrink-0 mt-1" />
                    <span className="text-primary/90 leading-relaxed">{w}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {fees.length > 0 && (
          <section className="border-t border-border bg-secondary/15 py-16 lg:py-24">
            <div className="mx-auto max-w-6xl px-6 lg:px-10">
              <div className="text-center">
                <div className="overline">Management fees</div>
                <h2 className="font-serif-display text-4xl text-primary mt-3">Simple, competitive rates</h2>
              </div>
              <div className={`mt-12 grid grid-cols-1 ${fees.length >= 3 ? "lg:grid-cols-3" : "sm:grid-cols-2 max-w-3xl mx-auto"} gap-6`}>
                {fees.map((f, i) => (
                  <div
                    key={i}
                    className={`rounded-sm p-7 border ${f.featured ? "border-accent bg-card shadow-sm" : "border-border bg-card"}`}
                  >
                    {f.featured && (
                      <div className="text-[10px] uppercase tracking-[0.2em] text-primary font-semibold mb-3">Most popular</div>
                    )}
                    <div className="font-serif-display text-2xl text-primary">{f.name}</div>
                    <div className="font-serif-display text-3xl text-primary mt-3">{f.price_label}</div>
                    {f.detail && <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{f.detail}</p>}
                    {f.includes?.length > 0 && (
                      <ul className="mt-5 space-y-2.5">
                        {f.includes.map((inc, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-primary/85">
                            <CheckCircle2 className="w-3.5 h-3.5 text-accent-ink shrink-0 mt-0.5" /> {inc}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="border-t border-border py-20 lg:py-24">
          <div className="mx-auto max-w-4xl px-6 lg:px-10 text-center">
            <h2 className="font-serif-display text-4xl text-primary">Ready to see what your property could earn?</h2>
            <p className="text-muted-foreground mt-5 leading-relaxed">
              {isUs
                ? "Request a free, no-obligation rent analysis: we'll get back to you the same day."
                : "Book a free, no-obligation rental valuation. We'll come back to you the same day."}
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Link to={contactHref}>
                <Button size="lg" className="h-12 px-7 rounded-sm">
                  <Sparkles className="w-4 h-4 mr-2" />Get in touch
                </Button>
              </Link>
              <Link to={valuationHref}>
                <Button size="lg" variant="outline" className="h-12 px-7 rounded-sm border-primary/20">
                  {isUs ? "Home valuation" : "Instant valuation tool"}
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

/**
 * Which design this agency's site is built in decides which of these
 * renders. See lib/siteTemplate.js: a per-agency setting, with `?theme=`
 * still winning so a preview can be reviewed before anybody is moved.
 */
export default function AgencyLet(props) {
  const brand = useBrand();
  const previewing = isPreviewing(brand);
  if (isEstate(brand)) return <EstateSellLet kind="let" preview={previewing} />;
  return <AgencyLetClassic {...props} />;
}
